import { useState } from 'react'
import { Rnd } from 'react-rnd'
import type { GroupBox as GroupBoxType } from '@/stores/customer-tracker-store'
import { useCustomerTrackerStore } from '@/stores/customer-tracker-store'
import { X, Edit2, GripVertical } from 'lucide-react'

interface GroupBoxProps {
  box: GroupBoxType
}

export function GroupBox({ box }: GroupBoxProps) {
  const { updateGroupBox, removeGroupBox, drawMode, customers, updatePosition } = useCustomerTrackerStore()
  const [isEditingLabel, setIsEditingLabel] = useState(false)
  const [labelValue, setLabelValue] = useState(box.label)
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null)
  const [customersInside, setCustomersInside] = useState<string[]>([])

  const handleLabelSave = () => {
    if (labelValue.trim()) {
      updateGroupBox(box.id, { label: labelValue.trim() })
    }
    setIsEditingLabel(false)
  }

  const handleLabelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLabelSave()
    } else if (e.key === 'Escape') {
      setLabelValue(box.label)
      setIsEditingLabel(false)
    }
  }

  const isCustomerInside = (customer: any, groupPos: { x: number; y: number }, groupSize: { width: number; height: number }) => {
    const cx = customer.position.x
    const cy = customer.position.y
    const gx = groupPos.x
    const gy = groupPos.y
    const gw = groupSize.width
    const gh = groupSize.height

    return cx >= gx && cx <= gx + gw && cy >= gy && cy <= gy + gh
  }

  return (
    <Rnd
      size={{ width: box.size.width, height: box.size.height }}
      position={{ x: box.position.x, y: box.position.y }}
      onDragStart={() => {
        setDragStart({ x: box.position.x, y: box.position.y })
        const inside = customers
          .filter((customer) => isCustomerInside(customer, box.position, box.size))
          .map((c) => c.id)
        setCustomersInside(inside)
      }}
      onDragStop={(_e, d) => {
        if (!dragStart) return

        const deltaX = d.x - dragStart.x
        const deltaY = d.y - dragStart.y

        updateGroupBox(box.id, { position: { x: d.x, y: d.y } })

        // Move all customers that were inside at drag start
        customersInside.forEach((customerId) => {
          const customer = customers.find((c) => c.id === customerId)
          if (customer) {
            updatePosition(customer.id, {
              x: customer.position.x + deltaX,
              y: customer.position.y + deltaY,
            })
          }
        })

        setDragStart(null)
        setCustomersInside([])
      }}
      onResizeStop={(_e, _direction, ref, _delta, position) => {
        updateGroupBox(box.id, {
          size: { width: ref.offsetWidth, height: ref.offsetHeight },
          position,
        })
      }}
      minWidth={150}
      minHeight={100}
      bounds="parent"
      className="group select-none"
      dragHandleClassName="drag-handle"
      style={{ pointerEvents: drawMode ? 'none' : 'auto' }}
    >
      <div
        className="w-full h-full border-2 rounded-lg relative"
        style={{
          borderColor: box.color,
          backgroundColor: `${box.color}08`,
        }}
      >
        {/* Top bar with drag handle, label, and controls */}
        <div className="absolute -top-8 left-0 right-0 flex items-center gap-2">
          {/* Drag handle */}
          <button
            className="drag-handle cursor-move p-1 hover:bg-white/10 rounded"
            style={{ color: box.color }}
          >
            <GripVertical className="w-4 h-4" />
          </button>

          {/* Label */}
          {isEditingLabel ? (
            <input
              type="text"
              value={labelValue}
              onChange={(e) => setLabelValue(e.target.value)}
              onBlur={handleLabelSave}
              onKeyDown={handleLabelKeyDown}
              autoFocus
              className="px-2 py-1 text-sm font-semibold bg-[#0b0c0d] border rounded text-white flex-1"
              style={{
                borderColor: box.color,
                color: box.color,
              }}
            />
          ) : (
            <>
              <span
                className="px-3 py-1 text-sm font-semibold rounded flex-1"
                style={{
                  backgroundColor: box.color,
                  color: 'white',
                }}
              >
                {box.label}
              </span>
              <button
                onClick={() => setIsEditingLabel(true)}
                className="p-1 hover:bg-white/10 rounded"
                style={{ color: box.color }}
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => removeGroupBox(box.id)}
                className="p-1 rounded bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </Rnd>
  )
}
